import React, { useRef } from 'react';
import { DeviceInfoPanel } from '../components/device/DeviceInfoPanel';
import { getRecoil } from 'recoil-nexus';
import 'allotment/dist/style.css';

import { useState, useEffect, useLayoutEffect } from 'react';
import {
  Box,
  Button,
  Drawer,
  Stack,
  styled,
  SwipeableDrawer,
} from '@mui/material';
import { Allotment, setSashSize } from 'allotment';
import { MapComponent } from '../components/device/MapComponent';
import { DeviceListComponent } from '../components/device/DeviceList';
import { device_controller } from '../../controllers/device_controller';
import { company_controller } from '../../controllers/company_controller';
import { selectedDeviceStateAtom } from '../../models/atoms/device_atoms';
import { NAVBAR_HEIGHT } from '../components/navbar/Navbar';
import { useRecoilState, useRecoilValue } from 'recoil';
import { FOOTER_HEIGHT } from '../components/Footer';
import { grey } from '@mui/material/colors';
import { useIsMobile } from '../../hooks/useIsMobile';
import { MobileFleetMetrics } from '../components/device/MobileFleetMetrics';

export const DeviceMetricsPage = (
  _: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >,
) => {
  const isMobile = useIsMobile();

  useEffect(() => {
    device_controller.get_device_list().then(() => {
      device_controller.get_device_list(() => {}, 'archived');
    });
    company_controller._get_labels();
    const selectedDevice = getRecoil(selectedDeviceStateAtom);
    if (selectedDevice) {
      device_controller.select_device(selectedDevice.device_id);
    }
  }, []);

  return isMobile ? <MobileLayout /> : <DesktopLayout />;
};

const PaneContainer = styled('div')(() => ({
  height: '100%',
  width: '100%',
  padding: '1rem',
}));

function DesktopLayout() {
  const [panelSizes, setPanelSizes] = useState({
    leftMin: 0,
    leftMax: 0,
    topMin: 0,
    topMax: 0,
  });

  const root = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    setSashSize(11);

    if (!root.current) {
      return;
    }

    const width = root.current?.clientWidth ?? 0;
    const height = root.current?.clientHeight ?? 0;
    setPanelSizes({
      leftMin: Math.floor(0.5 * width),
      leftMax: Math.floor(0.9 * width),
      topMin: Math.floor(0.1 * height),
      topMax: Math.floor(0.9 * height),
    });
  }, []);

  return (
    <div ref={root} style={{ height: '100%', width: '100%' }}>
      <Allotment>
        <Allotment.Pane
          minSize={panelSizes.leftMin}
          maxSize={panelSizes.leftMax}
          preferredSize="60%"
        >
          <Allotment vertical>
            <Allotment.Pane
              minSize={panelSizes.topMin}
              maxSize={panelSizes.topMax}
              preferredSize="50%"
            >
              <PaneContainer>
                <MapComponent />
              </PaneContainer>
            </Allotment.Pane>
            <Allotment.Pane preferredSize="50%">
              <PaneContainer>
                <DeviceListComponent />
              </PaneContainer>
            </Allotment.Pane>
          </Allotment>
        </Allotment.Pane>
        <Allotment.Pane preferredSize="40%">
          <PaneContainer>
            <DeviceInfoPanel />
          </PaneContainer>
        </Allotment.Pane>
      </Allotment>
    </div>
  );
}

function DeviceInfoDrawer(
  _: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >,
) {
  const [selectedDevice, setSelectedDevice] = useRecoilState(
    selectedDeviceStateAtom,
  );
  return (
    <Drawer
      anchor={'right'}
      open={selectedDevice !== null}
      onClose={() => setSelectedDevice(null)}
    >
      <Box flex={1} pt={`${NAVBAR_HEIGHT}px`} overflow="hidden">
        <DeviceInfoPanel />
      </Box>
    </Drawer>
  );
}

const StyledBox = styled('div')(() => ({
  backgroundColor: '#fff',
}));

const Puller = styled('div')(() => ({
  width: 30,
  height: 6,
  backgroundColor: grey[300],
  borderRadius: 3,
  position: 'absolute',
  top: 8,
  left: 'calc(50% - 15px)',
}));

function DeviceListDrawer(
  _: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >,
) {
  const drawerBleeding = FOOTER_HEIGHT;
  const [open, setOpen] = useState(false);
  const selectedDevice = useRecoilValue(selectedDeviceStateAtom);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  if (selectedDevice) {
    return <></>;
  }

  return (
    <Box
      sx={{
        minHeight: open ? `100%` : 2 * FOOTER_HEIGHT,
      }}
    >
      <Box
        sx={{
          textAlign: 'center',
          pt: 1,
          visibility: open ? 'hidden' : 'visible',
        }}
      >
        <Button onClick={toggleDrawer(true)}>Open</Button>
      </Box>
      <SwipeableDrawer
        anchor={'bottom'}
        open={open}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        swipeAreaWidth={drawerBleeding}
        disableSwipeToOpen={false}
        keepMounted
        PaperProps={{
          sx: {
            height: `calc(100% - ${drawerBleeding}px - ${NAVBAR_HEIGHT}px)`,
            overflow: 'visible',
          },
        }}
        onClick={toggleDrawer(!open)}
      >
        <StyledBox
          sx={{
            position: 'absolute',
            top: -drawerBleeding,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            visibility: 'visible',
            right: 0,
            left: 0,
            height: '100%',
            paddingTop: '1rem',
          }}
        >
          <Puller />
          <MobileFleetMetrics />
          <DeviceListComponent />
        </StyledBox>
      </SwipeableDrawer>
    </Box>
  );
}

function MobileLayout(
  _: React.DetailedHTMLProps<
    React.HTMLAttributes<HTMLDivElement>,
    HTMLDivElement
  >,
) {
  return (
    <Stack height={'100%'} overflow="auto">
      <Box id="mapID" display={'flex'} flex={1} minHeight="50%" width="100%">
        <MapComponent />
      </Box>
      <DeviceListDrawer />
      <DeviceInfoDrawer />
    </Stack>
  );
}
